import React, { useState, useEffect } from 'react';

import PropTypes from 'prop-types';
import {
    Drawer,
    Descriptions,
    Typography,
    Tag,
    Spin,
    Dropdown,
    Menu,
    message,
    Space,
    Table,
} from 'antd';
import { DownOutlined } from '@ant-design/icons';
// import { CreditCardOutlined } from '@ant-design/icons';

import { formatDate } from '../../utils/format';
import { api } from '../../services/authService';
import { RenderIf } from '../RenderIf';

const { Title } = Typography;

const countries: Record<string, string> = {
    US: 'United States',
    UK: 'United Kingdom',
    GB: 'United Kingdom',
    CA: 'Canada',
    AU: 'Australia',
    NG: 'Nigeria',
    GH: 'Ghana',
    KE: 'Kenya',
    // Add more as needed
};

const UserProfileDrawer = ({
    visible,
    onClose,
    user,
    userType,
    loading,
    currentUser,
    onStatusUpdate,
}: any) => {
    // const [userCards, setUserCards] = useState([]);
    // const [loadingCards, setLoadingCards] = useState(false);

    // useEffect(() => {
    //     if (visible && user?.id) {
    //         fetchUserCards();
    //     }
    // }, [visible, user?.id]);

    // const fetchUserCards = async () => {
    //     try {
    //         setLoadingCards(true);
    //         const response = await api.get(`/admin/cards?user_id=${user.id}`);
    //         setUserCards(response.data.cards || []);
    //     } catch (error) {
    //         console.error('Error fetching user cards:', error);
    //     } finally {
    //         setLoadingCards(false);
    //     }
    // };

    // Helper function to get country name from country code
    const getCountryName = (countryCode: string) => {
        if (!countryCode) return 'N/A';

        // If it's already a full name, return it
        if (countryCode.length > 2) return countryCode;

        // This would ideally use a proper country code to name mapping
        // For now, just return the code as-is if it's not obvious

        return countries[countryCode] || countryCode;
    };

    const renderBusinessInfo = () => {
        if (!user || userType !== 'business') return null;

        return (
            <>
                <Descriptions.Item label="ID">
                    {user.id || 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Email">
                    {user.email || 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Business Domain">
                    {user.business_domain || 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Business Name">
                    {user.business_name || 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Business Type">
                    {user.legal_structure || 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Industry">
                    {user.industry || 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Country">
                    {getCountryName(user.country) || 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Status">
                    <Tag
                        color={
                            user.status === 'approved'
                                ? 'success'
                                : user.status === 'pending'
                                ? 'warning'
                                : user.status === 'declined'
                                ? 'error'
                                : 'default'
                        }
                    >
                        {user.status?.toUpperCase() || 'N/A'}
                    </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Created">
                    {formatCreatedDate(user.created) || 'N/A'}
                </Descriptions.Item>
            </>
        );
    };

    const renderIndividualInfo = () => {
        if (!user || userType !== 'individual') return null;

        return (
            <>
                <Descriptions.Item label="ID">
                    {user.id || 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="First Name">
                    {user.firstname || 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Last Name">
                    {user.lastname || 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Email">
                    {user.email || 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Phone Number">
                    {formatPhoneNumber(user.phone_number) || 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Status">
                    <Tag
                        color={
                            user.status === 'approved'
                                ? 'success'
                                : user.status === 'pending-review'
                                ? 'warning'
                                : user.status === 'declined'
                                ? 'error'
                                : 'default'
                        }
                    >
                        {user.status?.toUpperCase() || 'N/A'}
                    </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Is Activated">
                    <Tag color={user.is_activated ? 'success' : 'error'}>
                        {user.is_activated ? 'YES' : 'NO'}
                    </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Country">
                    {getCountryName(user.country) || 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Type">
                    {user.type || 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Created">
                    {formatCreatedDate(user.created) || 'N/A'}
                </Descriptions.Item>
            </>
        );
    };

    const formatPhoneNumber = (phone: string) => {
        if (!phone) return 'N/A';
        // Remove any non-digit characters except + for international format
        const cleaned = phone.replace(/[^\d+]/g, '');
        if (!cleaned) return 'N/A';

        // Handle international format
        if (cleaned.startsWith('+')) {
            return phone;
        }

        // Format as local number if exactly 10 digits
        if (cleaned.length === 10) {
            return cleaned.replace(/^(\d{3})(\d{3})(\d{4})$/, '($1) $2-$3');
        }

        // Return as is for other formats
        return phone;
    };

    // Function to safely format the created date
    const formatCreatedDate = (timestamp: number) => {
        if (!timestamp) return 'N/A';

        // Convert to number if it's a string
        if (typeof timestamp === 'string') {
            timestamp = parseInt(timestamp, 10);
        }

        // Ensure it's a valid number
        if (isNaN(timestamp)) return 'N/A';

        // If timestamp is in seconds (Unix timestamp), convert to milliseconds
        // Most Unix timestamps are 10 digits (seconds), while JS timestamps are 13 digits (milliseconds)
        if (timestamp < 10000000000) {
            timestamp *= 1000;
        }

        try {
            // Use the formatDate function with a specific format
            return formatDate(timestamp, 'DD/MM/YYYY HH:mm:ss');
        } catch (e) {
            console.error('Error formatting date:', e, timestamp);
            return 'N/A';
        }
    };

    // Determine the user's name based on type and available fields
    const getUserName = () => {
        if (!user) return 'User';

        if (userType === 'individual') {
            const firstName = user.firstname || '';
            const lastName = user.lastname || '';
            if (firstName || lastName) {
                return `${firstName} ${lastName}`.trim();
            }
        }

        return user.name || user.business_name || 'User';
    };

    // const renderCardsInfo = () => {
    //     if (!userCards.length) {
    //         return (
    //             <Descriptions.Item label="Cards">
    //                 No cards found
    //             </Descriptions.Item>
    //         );
    //     }

    //     const virtualCards = userCards.filter(card => card.card_type === 'virtual');
    //     const physicalCards = userCards.filter(card => card.card_type === 'physical');

    //     return (
    //         <>
    //             <Descriptions.Item label="Virtual Cards">
    //                 <Space direction="vertical" size="small">
    //                     <Tag color="blue">Count: {virtualCards.length}</Tag>
    //                     {virtualCards.map(card => (
    //                         <div key={card.id}>
    //                         <Space>
    //                             <CreditCardOutlined />
    //                             <span>**** **** **** {card.last_four}</span>
    //                             <Tag color={
    //                                 card.status === 'issued' ? 'success' :
    //                                 card.status === 'pending-collection' ? 'warning' :
    //                                 card.status === 'dispatched' ? 'processing' : 'default'
    //                             }>
    //                                 {card.status?.toUpperCase()}
    //                             </Tag>
    //                         </Space>
    //                     </div>
    //                     ))}
    //                 </Space>
    //             </Descriptions.Item>
    //             <Descriptions.Item label="Physical Cards">
    //                 <Space direction="vertical" size="small">
    //                     <Tag color="green">Count: {physicalCards.length}</Tag>
    //                     {virtualCards.map(card => (
    //                         <div key={card.id}>
    //                         <Space>
    //                             <CreditCardOutlined />
    //                             <span>**** **** **** {card.last_four}</span>
    //                             <Tag color={
    //                                 card.status === 'issued' ? 'success' :
    //                                 card.status === 'pending-collection' ? 'warning' :
    //                                 card.status === 'dispatched' ? 'processing' : 'default'
    //                             }>
    //                                 {card.status?.toUpperCase()}
    //                             </Tag>
    //                         </Space>
    //                     </div>
    //                     ))}
    //                 </Space>
    //             </Descriptions.Item>
    //         </>
    //     );
    // };

    return (
        <Drawer
            title={`${getUserName()} Profile`}
            placement="right"
            onClose={onClose}
            open={visible}
            width={500}
        >
            <RenderIf condition={loading}>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100%',
                    }}
                >
                    <Spin size="large" />
                </div>
            </RenderIf>
            <RenderIf condition={!loading}>
                {user ? (
                    <>
                        <Title level={4}>{getUserName()}</Title>
                        <Descriptions
                            bordered
                            column={1}
                        >
                            {userType === 'individual' &&
                                renderIndividualInfo()}
                            {userType === 'business' && renderBusinessInfo()}
                            {/* {renderCardsInfo()} */}
                        </Descriptions>
                    </>
                ) : (
                    <div>No user data available</div>
                )}
            </RenderIf>
        </Drawer>
    );
};

UserProfileDrawer.propTypes = {
    visible: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    user: PropTypes.object,
    userType: PropTypes.oneOf(['individual', 'business', 'admin']).isRequired,
    loading: PropTypes.bool,
    currentUser: PropTypes.object,
    onStatusUpdate: PropTypes.func,
};

UserProfileDrawer.defaultProps = {
    user: null,
    userType: 'individual',
    loading: false,
    currentUser: null,
    onStatusUpdate: () => {},
};

export default UserProfileDrawer;
